import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["onClick"]
const _hoisted_2 = ["onClick"]

import { AggregateBusinessFilterProperty, FilterType, isFetchRequiredFilterType } from '@/coreDomainsModule/entities/filter';
import { FilterInfoState } from './interface/uiState';
import { IPagingOptions } from '@/coreDomainsModule/paging';
import BaseSelectInput from './BaseSelectInput.vue';
import { ref, watch, computed, nextTick } from 'vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { i18n } from '@/i18n';
import { useSelectInputStore } from './useSelectInputStore';
import { storeToRefs } from 'pinia';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { lineClampSizes } from '@/coreDomainsModule/lineClamp';
import { SelectInputViewModel } from './selectInputViewModel';
import { FilterModalType } from '../filterModal/filterModal';


export default /*@__PURE__*/_defineComponent({
  __name: 'MultipleSelectInput',
  props: {
    index: {},
    selectedList: {},
    placeholder: {},
    label: {},
    disabled: { type: Boolean },
    filterType: {},
    isOpenProp: { type: Boolean },
    propAggregateBusinessFilterProperty: { default: undefined },
    propOptions: { default: () => [] as string[] },
    propPagingState: { default: undefined },
    propPagingOptions: { default: undefined },
    propSelectedIndexes: { default: undefined },
    propFilteredOptions: { default: undefined },
    isError: { type: Boolean },
    errorMessage: { default: '' },
    useTeleport: { type: Boolean },
    filterModalType: { default: 'deletion-business' },
    useSearchInput: { type: Boolean, default: true },
    useReset: { type: Boolean, default: true }
  },
  emits: ["click:selectInput", "update:modelValue", "update:valueWithIndexes", "click:seeMore", "close", "search"],
  setup(__props: any, { emit: __emit }) {

const vm = new SelectInputViewModel();
const t = i18n.t;

const props = __props;

const store = useSelectInputStore(props.filterType, props.filterModalType)();
const { state: baseSelectState } = storeToRefs(store);

const emit = __emit;

const selectedList = ref<string[]>([...props.selectedList]);
const selectedIndexes = ref<number[]>(props.propSelectedIndexes ?? []);
const selectedRepresent = ref<string>(props.selectedList[0] ?? '');

const hasSelectedItems = computed(() => selectedList.value.length > 0);

watch(
  () => props.selectedList,
  () => {
    selectedRepresent.value = props.selectedList[0];
    selectedList.value = props.selectedList;
  }
);

watch(
  () => props.propSelectedIndexes,
  () => {
    selectedIndexes.value = props.propSelectedIndexes ?? [];
  }
);

/**
 * 他のポップアップ要素の位置を再計算する
 */
const recalculatePopupPositions = () => {
  // VueDatePickerなどの固定位置要素を再配置
  nextTick(() => {
    // リサイズイベントを発火してポップアップ位置を再計算
    window.dispatchEvent(new Event('resize'));

    // VueDatePickerが存在する場合は直接再配置
    const datePickerMenus = document.querySelectorAll('.dp__menu');
    datePickerMenus.forEach((menu) => {
      const menuElement = menu as HTMLElement;
      if (menuElement.style.display !== 'none') {
        // メニューが表示されている場合、位置を再計算
        const trigger = document.querySelector('.dp__input_wrap');
        if (trigger) {
          const rect = trigger.getBoundingClientRect();
          menuElement.style.left = `${rect.left}px`;
          menuElement.style.top = `${rect.top - menuElement.offsetHeight - 8}px`;
        }
      }
    });
  });
};

/**
 * 選択されたオプションのリストを親コンポーネントに通知します。
 * @param option - 選択されたオプション
 * @param index - 選択されたオプションのインデックス
 */
const selectOption = (option: string, index?: number) => {
  const optionIndex = selectedList.value.indexOf(option);
  if (optionIndex > -1) {
    selectedList.value.splice(optionIndex, 1);
  } else {
    selectedList.value.push(option);
  }
  // インデックスが指定されている場合は、選択されたオプションのインデックスを保持する
  if (index !== undefined) {
    let overallIndex = index;
    if (props.propFilteredOptions) {
      // フィルタしている場合、そのままだとフィルタ後のものに対してのindexになるので変更する
      overallIndex = props.propOptions.indexOf(option);
    }
    const indexIndex = selectedIndexes.value.indexOf(overallIndex);
    if (indexIndex > -1) {
      selectedIndexes.value.splice(indexIndex, 1);
    } else {
      selectedIndexes.value.push(overallIndex);
    }
  }
  selectedRepresent.value = selectedList.value[0] ?? '';
  emit('update:modelValue', selectedList.value);
  if (index !== undefined) emit('update:valueWithIndexes', selectedList.value, selectedIndexes.value);

  // 選択変更後にポップアップ位置を再計算
  recalculatePopupPositions();
};

/**
 * すべてのオプションを選択状態にします。
 */
const selectAllOptions = () => {
  let allOptions: string[] = [];
  if (props.propFilteredOptions ? props.propFilteredOptions.length > 0 : props.propOptions.length > 0) {
    allOptions = [...(props.propFilteredOptions ?? props.propOptions)];
  } else if (isFetchRequiredFilterType(props.filterType) && baseSelectState.value.pagingState.type === 'success') {
    allOptions = [...baseSelectState.value.pagingState.data];
  } else if (!isFetchRequiredFilterType(props.filterType) && baseSelectState.value.options.length > 0) {
    allOptions = [...baseSelectState.value.options];
  }

  if (allOptions.length > 0) {
    selectedList.value = [...allOptions];
    // 全オプションに対応するインデックスを生成 (0 から始まる連番)
    selectedIndexes.value = allOptions.map((_, i) => i);
    selectedRepresent.value = selectedList.value[0] ?? '';

    emit('update:modelValue', selectedList.value);
    emit('update:valueWithIndexes', selectedList.value, selectedIndexes.value);

    // 全選択後にポップアップ位置を再計算
    recalculatePopupPositions();
  }
};

/**
 * 選択状態に応じて、選択を解除するか、すべて選択します。
 */
const handleReset = () => {
  if (hasSelectedItems.value) {
    resetSelectedOption();
  } else {
    selectAllOptions();
  }
};

/**
 * 選択されたオプションのリストをリセットします。
 */
const resetSelectedOption = () => {
  selectedList.value = [];
  selectedIndexes.value = [];
  selectedRepresent.value = '';

  emit('update:modelValue', []);
  emit('update:valueWithIndexes', [], []);

  // リセット後にポップアップ位置を再計算
  recalculatePopupPositions();
};

/**
 * 選択されたオプションを削除します。
 * @param option - 削除するオプション
 * @param index - 削除するオプションのインデックス
 */
const removeSelectedOption = (option: string, index?: number) => {
  selectOption(option, index);
  emit('close');
};

const confirmButtonBottom = ref<number>(0);

/**
 * emitから確定ボタンのスタイルを決定する関数
 * @param bottom - 下要素(SelectedList)の高さ
 */
const setConfirmButtonStyle = (bottom?: number) => {
  confirmButtonBottom.value = bottom ?? 0;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseSelectInput, {
    index: _ctx.index,
    "selected-represent": selectedRepresent.value,
    placeholder: _ctx.placeholder,
    label: _ctx.label,
    disabled: _ctx.disabled,
    "filter-type": _ctx.filterType,
    "is-open-prop": _ctx.isOpenProp,
    "is-continue-opening": true,
    "prop-aggregate-business-filter-property": _ctx.propAggregateBusinessFilterProperty,
    "prop-options": _ctx.propFilteredOptions ?? _ctx.propOptions,
    "prop-original-options": _ctx.propOptions,
    "prop-paging-state": _ctx.propPagingState,
    "prop-paging-options": _ctx.propPagingOptions,
    "is-error": _ctx.isError,
    "error-message": _ctx.errorMessage,
    "use-teleport": _ctx.useTeleport,
    "filter-modal-type": _ctx.filterModalType,
    "reset-button-text": hasSelectedItems.value ? _unref(t)('選択を外す') : _unref(t)('全選択する'),
    "use-search-input": _ctx.useSearchInput,
    "use-reset": _ctx.useReset,
    "onClick:selectInput": _cache[0] || (_cache[0] = () => _ctx.$emit('click:selectInput')),
    onReset: handleReset,
    onUpdate: _cache[1] || (_cache[1] = (e, index) => selectOption(e, index)),
    "onClick:seeMore": _cache[2] || (_cache[2] = () => _ctx.$emit('click:seeMore')),
    onClose: _cache[3] || (_cache[3] = () => _ctx.$emit('close')),
    onSelectedListResized: _cache[4] || (_cache[4] = 
      (bottom) => {
        setConfirmButtonStyle(bottom);
      }
    ),
    onSearch: _cache[5] || (_cache[5] = (keyword) => _ctx.$emit('search', keyword))
  }, {
    selectedListTemplate: _withCtx((selectedListProp) => [
      (selectedList.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "select-box__list--wrapper",
            style: _normalizeStyle(selectedListProp.scroll ? { overflow: 'scroll' } : {})
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedList.value, (selected) => {
              return (_openBlock(), _createElementBlock("div", {
                key: selected.key,
                class: "select-box__list--item"
              }, [
                _createElementVNode("div", {
                  class: "select-box__list--item--text",
                  style: _normalizeStyle({ WebkitLineClamp: _unref(lineClampSizes)['XS'] })
                }, _toDisplayString(_unref(vm).displayValue(_ctx.filterType, selected)), 5),
                (selectedListProp.isOpen)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "select-box__list--item--close",
                      onClick: ($event: any) => (selectOption(selected))
                    }, [
                      _createVNode(SvgLoader, { name: "close" })
                    ], 8, _hoisted_1))
                  : (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      class: "select-box__list--item--close",
                      onClick: ($event: any) => (removeSelectedOption(selected))
                    }, [
                      _createVNode(SvgLoader, { name: "close" })
                    ], 8, _hoisted_2))
              ]))
            }), 128))
          ], 4))
        : _createCommentVNode("", true)
    ]),
    selectBoxOptionCheckboxTemplate: _withCtx((selectBoxOptionInnerProp) => [
      _createVNode(RowCheckBox, {
        "is-checked": selectedList.value.includes(selectBoxOptionInnerProp.option)
      }, null, 8, ["is-checked"])
    ]),
    confirmButtonTemplate: _withCtx((confirmButtonProp) => [
      _createElementVNode("div", {
        class: "select-box__confirm--wrapper",
        style: _normalizeStyle({ bottom: `${confirmButtonBottom.value}px` })
      }, [
        _createVNode(TextButton, {
          "button-style": 'btn-primary',
          size: "btn-sm",
          "on-click": confirmButtonProp.click,
          class: "select-box__confirm--button"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('確定')), 1)
          ], undefined, true),
          _: 2
        }, 1032, ["on-click"])
      ], 4)
    ]),
    _: 1
  }, 8, ["index", "selected-represent", "placeholder", "label", "disabled", "filter-type", "is-open-prop", "prop-aggregate-business-filter-property", "prop-options", "prop-original-options", "prop-paging-state", "prop-paging-options", "is-error", "error-message", "use-teleport", "filter-modal-type", "reset-button-text", "use-search-input", "use-reset"]))
}
}

})